<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import { MESSAGE_TYPES, CONTENT_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';

const { t } = useI18n();

const { content, attachments, contentAttributes, messageType, contentType } =
  useMessageContext();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const renderOriginal = ref(false);

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const FIELD_LABEL_KEYS = {
  first_name: 'CONVERSATION.LEAD_CAPTURE.FIRST_NAME',
  last_name: 'CONVERSATION.LEAD_CAPTURE.LAST_NAME',
  email: 'CONVERSATION.LEAD_CAPTURE.EMAIL',
  phone_number: 'CONVERSATION.LEAD_CAPTURE.PHONE',
};

function humanizeFieldName(name) {
  if (!name) return '';
  return String(name)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

const leadCaptureRows = computed(() => {
  if (contentType.value !== CONTENT_TYPES.INPUT_EMAIL) return [];
  const rows = contentAttributes.value?.submittedValues ?? [];
  if (!Array.isArray(rows) || !rows.length) return [];
  return rows.map(row => {
    const key = row.name;
    const labelKey = FIELD_LABEL_KEYS[key];
    return {
      name: key,
      label: labelKey ? t(labelKey) : humanizeFieldName(key),
      value: row.value,
    };
  });
});

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="text">
    <div class="gap-3 flex flex-col">
      <span v-if="isEmpty" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>
      <FormattedContent v-if="renderContent" :content="renderContent" />
      <TranslationToggle
        v-if="hasTranslations"
        class="-mt-3"
        :showing-original="renderOriginal"
        @toggle="handleSeeOriginal"
      />
      <AttachmentChips :attachments="attachments" class="gap-2" />
      <template v-if="isTemplate">
        <dl
          v-if="leadCaptureRows.length"
          class="mt-1 space-y-2 rounded-lg bg-n-alpha-3 px-3 py-2 text-sm"
        >
          <template v-for="row in leadCaptureRows" :key="row.name">
            <div>
              <dt class="text-n-slate-11">{{ row.label }}</dt>
              <dd class="text-n-slate-12 font-medium">{{ row.value }}</dd>
            </div>
          </template>
        </dl>
        <div
          v-else-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3 text-sm"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
